import { demoIds, demoYears } from './demoData';
import type { DemoId, DemoLocale, DemoObservation, DemoYear } from './demoData';
import geometry from './buildingTeaserGeometry.json';
import evidence from './buildingTeaserEvidence.json';
import scenarios from './buildingTeaserScenarios.json';

type TeaserPhotoKey = 'cornice' | 'corner' | 'window';
type TeaserScenarioImageKey = `${TeaserPhotoKey}-example-${2027 | 2028}`;
export type TeaserImageKey = 'building' | TeaserPhotoKey | TeaserScenarioImageKey;
export type TeaserBoundingBox = typeof evidence.cornice.boundingBox;
export type TeaserEvidence = typeof evidence.cornice;
export interface TeaserObservation extends DemoObservation {
  location: string;
  photo: TeaserPhotoKey;
  photoAlt: string;
  source: TeaserEvidence;
  boundingBox: TeaserBoundingBox;
  expertRecommendation: string;
  year: DemoYear;
  media: { kind: 'documented' } | { kind: 'illustrative'; image: TeaserScenarioImageKey; boundingBox: TeaserBoundingBox } | { kind: 'unavailable' };
}

const legacyKeys = ['property', 'year', 'resolved', 'reopened', 'detail'];

export const buildingTeaserViews = ['existing', '3d', '2d'] as const;
export type BuildingTeaserView = typeof buildingTeaserViews[number];

export function parseBuildingTeaserView(search: string): BuildingTeaserView {
  const value = new URLSearchParams(search).get('view');
  return buildingTeaserViews.find(view => view === value) ?? 'existing';
}

export function parseBuildingTeaserYear(search: string): DemoYear {
  const value = new URLSearchParams(search).get('year');
  return demoYears.find(year => String(year) === value) ?? 2026;
}

export const teaserDisclaimer: Record<DemoLocale, string> = {
  sv: 'Bilderna visar dokumenterade skador. Välj en markör för att se skadan i närbild.',
  en: 'The photos show documented damage. Select a marker to see the damage up close.',
};

export const teaserScenarioNote: Record<DemoLocale, string> = {
  sv: 'Exempel på skadeutveckling. 2026 visar dokumenterad skada. 2027 och 2028 är AI-genererade illustrationer utifrån respektive källbild, inte nya inspektioner, en prognos eller verifierad försämring. Rutorna i illustrationerna är manuella exempelmarkeringar.',
  en: 'Example damage development. 2026 shows documented damage. 2027 and 2028 are AI-generated illustrations based on each source photo, not new inspections, a forecast or verified deterioration. Boxes in the illustrations are manually placed example annotations.',
};

export function teaserPeriodLabel(item: TeaserObservation, locale: DemoLocale): string {
  return item.year === 2026 ? teaserPhotoDate(locale) : `${locale === 'sv' ? 'Exempelår' : 'Example year'} ${item.year}`;
}

export function teaserMissingImage(locale: DemoLocale, year: DemoYear): string {
  return locale === 'sv'
    ? `Ingen bild eller utvecklingssekvens finns för detta fynd ${year} i exemplet. Det betyder inte att skadan är oförändrad eller åtgärdad. Välj 2026 för den dokumenterade bilden.`
    : `No image or development sequence is available for this finding in example year ${year}. This does not mean the damage is unchanged or repaired. Select 2026 for the documented photo.`;
}

export function teaserPhotoLabel(item: TeaserObservation, locale: DemoLocale): string {
  if (item.media.kind === 'unavailable') return `${item.id} · ${locale === 'sv' ? 'Bild saknas' : 'Image unavailable'} · ${item.year}`;
  const label = `${locale === 'sv' ? 'Bild' : 'Photo'} ${item.source.photoReference} · ${item.id}`;
  return item.media.kind === 'illustrative' ? `${label} · Illustration ${item.year}` : label;
}

export function teaserCropBox(source: TeaserEvidence): TeaserBoundingBox {
  const { boundingBox: box, crop, sourceWidth, sourceHeight } = source;
  return {
    left: (box.left * sourceWidth - crop.left) / crop.width,
    top: (box.top * sourceHeight - crop.top) / crop.height,
    width: box.width * sourceWidth / crop.width,
    height: box.height * sourceHeight / crop.height,
  };
}

export function isLegacyBuildingDemo(search: string): boolean {
  const params = new URLSearchParams(search);
  return !params.has('example') && legacyKeys.some(key => params.has(key));
}

export function parseBuildingTeaser(search: string): { selected: DemoId; year: DemoYear; invalid: boolean } {
  const params = new URLSearchParams(search);
  const selection = params.get('observation');
  const selected = demoIds.find(id => id === selection);
  const view = params.get('view');
  const yearText = params.get('year');
  const year = demoYears.find(value => String(value) === yearText);
  return {
    selected: selected || 'F-001',
    year: year || 2026,
    invalid: (selection !== null && !selected)
      || (params.has('example') && params.get('example') !== 'corner')
      || (view !== null && !buildingTeaserViews.some(item => item === view))
      || (yearText !== null && !year) || params.getAll('year').length > 1
      || legacyKeys.some(key => key !== 'year' && params.has(key)),
  };
}

export function buildingTeaserSearch(selected: DemoId, search = '', year?: DemoYear, view?: BuildingTeaserView): string {
  const params = new URLSearchParams(search);
  const comparisonYear = year ?? (params.get('example') === 'corner' ? parseBuildingTeaserYear(search) : 2026);
  legacyKeys.forEach(key => params.delete(key));
  params.set('example', 'corner');
  params.set('observation', selected);
  if (view === 'existing') params.delete('view');
  else if (view !== undefined) params.set('view', view);
  if (comparisonYear !== 2026) params.set('year', String(comparisonYear));
  return `?${params.toString()}`;
}

export function teaserPhotoDate(locale: DemoLocale): string {
  return new Date('2026-06-13T12:00:00Z').toLocaleDateString(locale === 'sv' ? 'sv-SE' : 'en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

export function teaserImageAsset(name: TeaserImageKey) {
  return {
    src: `/images/building-pass/${name}.jpg`,
    srcSet: [480, 960, 1440].map(width => `/images/building-pass/${name}-${width}.webp ${width}w`).join(', '),
    width: 1440,
    height: name === 'building' ? 1080 : 960,
  };
}

export function buildingTeaserObservations(locale: DemoLocale, year: DemoYear = 2026): TeaserObservation[] {
  const entries: Record<DemoLocale, Array<Pick<TeaserObservation, 'title' | 'location' | 'photo' | 'photoAlt' | 'assessment' | 'next' | 'expertRecommendation'>>> = {
    sv: [
      {
        title: 'Spricka i fasadputsen på hög höjd', location: 'Fasad A · övre fasadparti', photo: 'cornice',
        photoAlt: 'Närbild av en spricka i fasadputsen på hög höjd med befintlig skademarkering',
        assessment: 'En spricka syns i den putsade fasadytan under takfoten. Bilden visar sprickans förlopp men fastställer inte dess djup eller orsak.',
        next: 'Följ upp den markerade sprickan med en riktad närkontroll. Dokumentera utbredningen och jämför med kommande bilder.',
        expertRecommendation: 'Utred om sprickan är begränsad till putsen eller fortsätter i underlaget. En fasadkunnig ingenjör kan bedöma behovet av fortsatt undersökning och ge underlag för nästa steg.',
      },
      {
        title: 'Diagonal spricka på långsidan', location: 'Fasad A · långsidans övre del', photo: 'corner',
        photoAlt: 'Närbild av en diagonal spricka i långsidans övre fasadputs med befintlig skademarkering',
        assessment: 'En diagonal spricka löper genom den putsade ytan högt upp på långsidan. Markeringen visar en dokumenterad del av sprickan.',
        next: 'Dokumentera hela sprickans förlopp och följ upp vid en närkontroll. Jämför med tidigare bilder om sådana finns.',
        expertRecommendation: 'Bedöm sprickans sammanhang med fasadens konstruktion och anslutningar. Planera säker åtkomst för närkontroll och utred skadeorsaken innan en reparationsmetod väljs.',
      },
      {
        title: 'Setting vid fönster', location: 'Fasad B · fönsterdetalj', photo: 'window',
        photoAlt: 'Närbild vid fönster med portalens befintliga Setting-markering',
        assessment: 'Området vid fönstret är dokumenterat med klassen Setting. Klassnamnet är en registrerad observation, inte en fastställd skadeorsak.',
        next: 'Granska det markerade området och komplettera med närbilder för att tydliggöra avvikelsens omfattning.',
        expertRecommendation: 'Låt en fasadkunnig ingenjör bedöma murverk och fogar kring öppningen. Kontrollera på plats vad avvikelsen innebär innan åtgärder planeras.',
      },
    ],
    en: [
      {
        title: 'Crack in the upper façade render', location: 'Façade A · upper section', photo: 'cornice',
        photoAlt: 'Close-up of a crack in the upper façade render with its existing damage annotation',
        assessment: 'A crack is visible in the rendered façade below the eaves. The photo shows its path but does not establish its depth or cause.',
        next: 'Follow up the marked crack with a targeted close inspection. Document its extent and compare with future photos.',
        expertRecommendation: 'Investigate whether the crack is confined to the render or continues into the substrate. A façade engineer can assess the need for further investigation and provide a basis for the next step.',
      },
      {
        title: 'Diagonal crack on the long façade', location: 'Façade A · upper long side', photo: 'corner',
        photoAlt: 'Close-up of a diagonal crack in the upper render on the long façade with its existing damage annotation',
        assessment: 'A diagonal crack runs through the rendered surface high on the long façade. The box shows a documented section of the crack.',
        next: 'Document the full path of the crack and follow up with a close inspection. Compare with earlier photos if available.',
        expertRecommendation: 'Assess the crack in the context of the façade construction and junctions. Plan safe access for close inspection and investigate the cause before choosing a repair method.',
      },
      {
        title: 'Setting by a window', location: 'Façade B · window detail', photo: 'window',
        photoAlt: 'Window close-up with the existing Setting annotation from the portal',
        assessment: 'The area by the window is documented under the class Setting. This class is a recorded observation, not an established cause of damage.',
        next: 'Review the marked area and add close-up photos to clarify the extent of the deviation.',
        expertRecommendation: 'Ask a façade engineer to assess the masonry and joints around the opening. Check on site what the deviation represents before planning remedial work.',
      },
    ],
  };
  return entries[locale].map((entry, index): TeaserObservation => {
    const id = demoIds[index];
    const source = evidence[entry.photo];
    const observation: TeaserObservation = {
      ...entry, id, ...geometry[id], source, boundingBox: teaserCropBox(source),
      year, media: { kind: 'documented' },
      priority: source.severity === 'high', resolved: false,
      status: locale === 'sv' ? 'Dokumenterad observation' : 'Documented observation',
    };
    if (year === 2026) return observation;
    const later = year === 2028;
    const en = locale === 'en';
    const illustrated: TeaserObservation = {
      ...observation,
      media: { kind: 'illustrative', image: `${entry.photo}-example-${year}`, boundingBox: scenarios[entry.photo][year].boundingBox },
      photoAlt: en ? `Illustrated crack development, example year ${year}` : `Illustrerad sprickutveckling, exempelår ${year}`,
      status: en ? later ? 'More pronounced branching · example' : 'Wider crack · example'
        : later ? 'Ökad förgrening · exempel' : 'Bredare spricka · exempel',
      assessment: en
        ? later ? 'The edited image illustrates a wider crack with additional branching compared with example year 2027.'
          : 'The edited image illustrates a wider crack and an initial branch compared with the documented 2026 photo.'
        : later ? 'Den bearbetade bilden illustrerar en bredare spricka med fler förgreningar än i exempelåret 2027.'
          : 'Den bearbetade bilden illustrerar en bredare spricka och en första förgrening jämfört med den dokumenterade bilden från 2026.',
      next: en
        ? later ? 'In this scenario, prioritise an engineering investigation rather than only continued photo follow-up. Decide the next step from verified observations on site.'
          : 'In this scenario, flag the visible change for review. Obtain comparable close-up images and arrange a targeted site inspection.'
        : later ? 'I detta scenario prioriteras en byggnadsteknisk utredning framför enbart fortsatt bilduppföljning. Välj nästa steg utifrån verifierade observationer på plats.'
          : 'I detta scenario flaggas den synliga förändringen för granskning. Komplettera med jämförbara närbilder och boka en riktad kontroll på plats.',
      expertRecommendation: en
        ? later ? 'Example investigation: establish the cause and extent in the render and substrate. Assess the need for temporary precautions and prepare technical documentation before selecting repairs.'
          : 'Example investigation: compare crack extent on site, examine the substrate and junctions, and determine what further investigation is needed.'
        : later ? 'Exempel på utredning: klarlägg orsak och omfattning i puts och underlag. Bedöm behov av tillfälliga skyddsåtgärder och ta fram tekniskt underlag innan reparation väljs.'
          : 'Exempel på utredning: jämför sprickans utbredning på plats, undersök underlag och anslutningar och avgör vilken fortsatt undersökning som behövs.',
    };
    if (entry.photo === 'corner') return {
      ...illustrated,
      photoAlt: en ? `Illustrated diagonal crack on the long façade, example year ${year}`
        : `Illustrerad diagonal spricka på långsidan, exempelår ${year}`,
      assessment: en
        ? later ? 'The illustration shows a more pronounced diagonal crack with several short branches in the upper render, compared with example year 2027.'
          : 'The illustration shows the documented diagonal crack with more visible edges and a short branch in the upper render.'
        : later ? 'Illustrationen visar en mer framträdande diagonal spricka med flera korta förgreningar i den övre fasadputsen jämfört med exempelåret 2027.'
          : 'Illustrationen visar den dokumenterade diagonala sprickan med tydligare kanter och en kort förgrening i den övre fasadputsen.',
      next: en
        ? later ? 'In this scenario, prioritise a targeted engineering investigation of the long façade. Verify the extent on site before planning remedial work.'
          : 'In this scenario, flag the change for review and document the full crack path with comparable close-up photos.'
        : later ? 'I detta scenario prioriteras en riktad byggnadsteknisk utredning av långsidan. Verifiera omfattningen på plats innan åtgärder planeras.'
          : 'I detta scenario flaggas förändringen för granskning. Dokumentera hela sprickans förlopp med jämförbara närbilder.',
      expertRecommendation: en
        ? later ? 'Example investigation: examine the render, substrate and junctions along the crack. Establish the cause and prepare technical documentation before choosing a repair method.'
          : 'Example investigation: plan safe access to the upper façade and assess the crack in relation to the construction and junctions.'
        : later ? 'Exempel på utredning: undersök puts, underlag och anslutningar längs sprickan. Klarlägg orsaken och ta fram tekniskt underlag innan reparationsmetod väljs.'
          : 'Exempel på utredning: planera säker åtkomst till den övre fasaden och bedöm sprickan i förhållande till konstruktion och anslutningar.',
    };
    if (entry.photo === 'window') return {
      ...illustrated,
      photoAlt: en ? `Illustrated stepped joint cracking by a window, example year ${year}`
        : `Illustrerad trappstegsformad fogspricka vid fönster, exempelår ${year}`,
      status: en ? later ? 'More pronounced joint opening · example' : 'Wider joint crack · example'
        : later ? 'Tydligare fogöppning · exempel' : 'Bredare fogspricka · exempel',
      assessment: en
        ? later ? 'The illustration shows a wider, darker stepped opening along the joints above the window than in example year 2027. It does not establish settlement or another cause.'
          : 'The illustration shows a more visible stepped crack along the masonry joints above the window. Setting remains the recorded class, not a confirmed settlement diagnosis.'
        : later ? 'Illustrationen visar en bredare och mörkare trappstegsformad öppning längs fogarna ovanför fönstret än i exempelåret 2027. Den fastställer inte sättning eller annan skadeorsak.'
          : 'Illustrationen visar en tydligare trappstegsformad spricka längs murverkets fogar ovanför fönstret. Setting är fortsatt registrerad klass, inte en fastställd sättningsdiagnos.',
      next: en
        ? later ? 'In this scenario, prioritise a masonry investigation around the opening. Base decisions on verified site observations, not the illustration.'
          : 'In this scenario, flag the joint change for review. Obtain comparable close-ups and arrange a targeted site inspection.'
        : later ? 'I detta scenario prioriteras en utredning av murverket kring öppningen. Utgå från verifierade observationer på plats, inte illustrationen.'
          : 'I detta scenario flaggas fogförändringen för granskning. Komplettera med jämförbara närbilder och boka en riktad kontroll på plats.',
      expertRecommendation: en
        ? later ? 'Example investigation: establish the cause and extent in the masonry and around the window arch. Assess the need for further investigation before selecting repairs.'
          : 'Example investigation: inspect the joints, bricks and window arch on site. Assess whether further investigation or movement monitoring is needed.'
        : later ? 'Exempel på utredning: klarlägg orsak och omfattning i murverket och kring fönstervalvet. Bedöm behovet av fortsatt undersökning innan reparation väljs.'
          : 'Exempel på utredning: kontrollera fogar, tegel och fönstervalv på plats. Bedöm om fortsatt undersökning eller rörelsemätning behövs.',
    };
    return illustrated;
  });
}
